import round from 'utils/round';
import React from 'react';
import TableCell from 'components/common/table/cell/TableCell';
import style from 'styles/styled-components/style';
import { HitField, ISearchDB } from 'type/db';
import { PlatformCode } from 'type/common';
import CoreTag, { ColorType, ITag } from 'shared/tag/CoreTag';
import { IIdCell } from 'components/common/table/cell/Id';
import { IProfileCell } from 'components/common/table/cell/Profile';
import { ICategoryCell } from 'components/common/table/cell/Category';

interface ISearchHeaderItem {
	width: number;
	margin: number;
}

interface ISearchHeader {
	icon: ISearchHeaderItem;
	id: ISearchHeaderItem;
	category: ISearchHeaderItem;
	follower: ISearchHeaderItem;
	tag: ISearchHeaderItem;
}

const useSearchResultTable = (): [
	ISearchHeader,
	Record<string, (data: ISearchDB) => JSX.Element>,
] => {
	const search_header: ISearchHeader = {
		icon: { width: 72, margin: 16 },
		id: { width: 205, margin: 32 },
		category: { width: 220, margin: 32 },
		follower: { width: 100, margin: 57 },
		tag: { width: 106, margin: 0 },
	};

	const platform_follower: Record<PlatformCode, string> = {
		ig: '팔로워',
		yt: '구독자',
		nb: '누적방문자',
		tt: '팔로워',
	};

	const tag_color: Record<HitField, ColorType> = {
		'아이디 일치': 'secondary',
		'유사 아이디': 'blue',
		'포스팅 키워드 발견': 'green',
		'댓글 키워드 발견': 'green',
		'비디오 키워드 발견': 'green',
		'프로필 키워드 발견': 'green',
		기타: 'yellow',
		'알 수 없음': 'gray',
	};

	const search_cell: Record<string, (data: ISearchDB) => JSX.Element> = {
		icon(data) {
			const props: IProfileCell = {
				platform_code: data.platform_code,
				username: data?.username ?? '',
				profile_url: data?.profile_url ?? '',
				_width: search_header.icon.width,
				_margin: `0 ${search_header.icon.margin}px 0 0`,
			};
			return <TableCell.Profile {...props} />;
		},
		id(data) {
			const props: IIdCell = {
				username: data?.username ?? '',
				full_name: data?.full_name ?? '',
				_width: search_header.id.width,
				_margin: `0 ${search_header.id.margin}px 0 0`,
			};
			return <TableCell.Id {...props} />;
		},
		category(data) {
			const props: ICategoryCell = {
				categories: [data?.category_1, data?.category_2],
				_width: search_header.category.width,
				_margin: `0 ${search_header.category.margin}px 0 0`,
				platform_code: data.platform_code,
			};
			return <TableCell.Category {...props} />;
		},
		follower(data) {
			const followerText = () => {
				if (data?.follower_count == null) return '-';

				const count =
					data?.platform_code === 'nb' ? data?.total_visitor ?? 0 : data?.follower_count;

				return `${round(count, 'up')} ${platform_follower[data?.platform_code]}`;
			};

			return (
				<style.Grid
					_width={`${search_header.follower.width}px`}
					_margin={`0 ${search_header.follower.margin}px 0 0`}
					flex="end"
				>
					<style.Text typo="span">{followerText()}</style.Text>
				</style.Grid>
			);
		},
		tag(data) {
			const children_props: ITag = {
				tag_type: 'text',
				color_type: tag_color[data?.hit_fields],
				value: data?.hit_fields,
			};
			const children =
				data?.hit_fields !== '알 수 없음'
					? React.createElement(CoreTag, children_props)
					: React.createElement(style.Text, { typo: 'span' }, '-');

			return (
				<style.Grid
					_width={`${search_header.follower.width}px`}
					flex_option="1 0"
					flex="end"
				>
					{children}
				</style.Grid>
			);
		},
	};

	return [search_header, search_cell];
};

export default useSearchResultTable;
